<template>
  <div class="print-list-page">
    <!-- 页面头部操作栏 -->
    <div class="page-actions no-print">
      <el-button type="primary" :icon="ArrowLeft" @click="goBack">返回</el-button>
      <el-button type="success" :icon="Printer" @click="handlePrint">打印</el-button>
      <el-button type="warning" :icon="Download" @click="handleExportExcel">导出Excel</el-button>
      <el-tooltip content="刷新打印详情" placement="left" class="page-refresh-action">
        <el-button
          type="primary"
          circle
          :icon="Refresh"
          :loading="loading"
          @click="handleRefreshPrintData"
        />
      </el-tooltip>
    </div>

    <div v-if="loading" class="print-loading-overlay">
      <el-skeleton :rows="10" animated />
    </div>

    <!-- 打印内容区域：加载时保持隐藏渲染，用于完成布局测量后整体显示 -->
    <div class="print-wrapper" :class="{ 'print-wrapper--preparing': loading }" ref="printContentRef">
      <template v-if="printData && printData.length > 0">
        <div 
          v-for="(item, pageIndex) in printData" 
          :key="pageIndex" 
          class="print-content"
          :class="{ 'landscape-content': item.isLandscape }"
        >
          <!-- 页面主体内容 -->
          <div class="page-body">
            <!-- 页眉信息 -->
            <div class="page-header-info">
              <span class="header-code">{{ formatDateCode(item.implementDate) }}</span>
              <span class="header-date">实施日期：{{ formatDate(item.implementDate) }}</span>
            </div>

            <!-- 公司标题 -->
            <div class="company-header">
              <div class="company-name">建研院检测中心有限公司</div>
              <div class="company-sub">国家建筑工程质量检验检测中心</div>
              <div class="report-title">{{ item.projectName }}</div>
              <div class="report-code">({{ item.controlNumber }})</div>
            </div>

            <!-- 基本信息行 -->
            <div class="info-row">
              <div class="info-item">
                <span class="info-label">记录编号：</span>
                <span class="info-value" :class="{ 'empty-value': !hasDisplayValue(item.recordNumber) }">{{ item.recordNumber }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">设备编号：</span>
                <span class="info-value" :class="{ 'empty-value': !hasDisplayValue(item.deviceNumber) }">{{ item.deviceNumber }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">环境温度</span>
                <span class="info-value" :class="{ 'empty-value': !hasDisplayValue(item.temperature) }">{{ item.temperature }}</span>
                <span class="info-label">℃</span>
              </div>
              <div class="info-item page-info">
                <span class="info-label">共</span>
                <span class="info-value page-value">{{ item.totalPagesInData }}</span>
                <span class="info-label">页第</span>
                <span class="info-value page-value">{{ item.pageIndexInData + 1 }}</span>
                <span class="info-label">页</span>
              </div>
            </div>

            <!-- 特殊属性行（只在首页显示；检测对象为"默认"的项不显示） -->
            <div class="special-attrs-row" v-if="item.isFirstPage && visibleSpecialAttributes(item).length > 0">
              <div 
                v-for="(attr, attrIndex) in visibleSpecialAttributes(item)"
                :key="attrIndex"
                class="special-attr-item"
              >
                <span class="attr-label">{{ attr.title }}</span>
                <span class="attr-value" :class="{ 'empty-value': !hasDisplayValue(attr.value) }">{{ attr.value }}</span>
              </div>
            </div>

            <!-- 续页标识 -->
            <div v-if="!item.isFirstPage" class="continue-page-hint">
              （续上页）
            </div>

            <!-- 数据表格 -->
            <div class="table-container" v-if="item.pageRows && item.pageRows.length > 0">
              <table class="data-table">
                <thead>
                  <!-- 表头（单行）：固定列 + 横向标题 -->
                  <tr v-if="item.tableInfo && item.tableInfo.horizontalHeaders">
                    <th class="col-seq">序<br/>号</th>
                    <th class="col-building">楼号<br/>楼层</th>
                    <th class="col-axis">轴线号</th>
                    <th class="col-vertical"></th>
                    <th
                      v-for="(title, hIndex) in item.tableInfo.horizontalHeaders"
                      :key="hIndex"
                      :class="hasDividerLine(item.tableInfo, hIndex) ? 'divider-column' : ''"
                      :width="120"
                    >
                        {{ getTransformedTitle(title, item.tableInfo, item.pageRows[0]) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(row, rowIndex) in item.pageRows" :key="rowIndex">
                    <tr
                      v-if="shouldRenderSingleAttrsRow(item, row, rowIndex)"
                      class="single-attrs-row"
                      :class="{ 'main-row-start': rowIndex > 0 }"
                    >
                      <td
                        class="col-seq"
                        :rowspan="getSeqRowspanInPage(item, rowIndex)"
                      >
                        {{ row.mainIndex + 1 }}
                      </td>
                      <td
                        class="single-attrs-cell"
                        :colspan="getTableColumnCount(item) - 1"
                      >
                        <div class="single-attrs-content">
                          <span
                            v-for="(attr, attrIndex) in getMainSingleAttrsList(item, row.mainIndex)"
                            :key="attrIndex"
                            class="single-attrs-item"
                          >
                            <span class="single-attrs-label">{{ attr.title }}</span>
                            <span class="single-attrs-value" :class="{ 'empty-value': !hasDisplayValue(attr.value) }">{{ attr.value }}</span>
                          </span>
                        </div>
                      </td>
                    </tr>
                    <tr
                                        :class="{ 'main-row-start': !hasMainSingleAttrs(item, row.mainIndex) && row.isFirstSubRow && rowIndex > 0 && (rowIndex === 0 || item.pageRows[rowIndex - 1]?.mainIndex !== row.mainIndex) }"
                      :style="getRowStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape)"
                                      >
                      <!-- 固定列：只在每个主表的第一个子行显示 -->
                      <td
                        v-if="shouldRenderSeqCell(item, row, rowIndex)"
                        class="col-seq"
                        :rowspan="getSeqRowspanInPage(item, rowIndex)"
                        :style="getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape)"
                      >
                        {{ row.mainIndex + 1 }}
                      </td>
                      <td
                        v-if="row.isFirstSubRow && isFirstRowOfMainInPage(item.pageRows, rowIndex)"
                        class="col-building"
                        :rowspan="getRowspanInPage(item.pageRows, rowIndex)"
                        :style="getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape)"
                      >
                        {{ row.buildingFloor }}
                      </td>
                      <td
                        v-if="row.isFirstSubRow && isFirstRowOfMainInPage(item.pageRows, rowIndex)"
                        class="col-axis fixed-col-end"
                        :rowspan="getRowspanInPage(item.pageRows, rowIndex)"
                        :style="getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape)"
                      >
                        {{ row.axisNumber }}
                      </td>
                      <!-- 纵向标题和数据列：每个子行都显示 -->
                      <td class="col-vertical" :style="getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape)">{{ row.verticalHeader }}</td>
                      <td
                        v-for="(cell, cellIndex) in row.cells"
                        :key="cellIndex"
                        :class="getSectionClassByIndex(item.sections, cellIndex)"
                        :style="getCellStyle(item.rowExtraHeights?.[rowIndex] || 0, item.isLandscape)"
                      >
                        {{ cell }}
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 页脚签名区 -->
          <div class="page-footer">
            <div class="footer-item">
              <span class="footer-label">检验：</span>
              <span class="footer-value" :class="{ 'empty-value': !hasDisplayValue(item.inspector) }">{{ item.inspector }}</span>
            </div>
            <div class="footer-item" v-if="item.showCalculator">
              <span class="footer-label">计算：</span>
              <span class="footer-value" :class="{ 'empty-value': !hasDisplayValue(item.calculator) }">{{ item.calculator }}</span>
            </div>
            <div class="footer-item">
              <span class="footer-label">校核：</span>
              <span class="footer-value" :class="{ 'empty-value': !hasDisplayValue(item.reviewer) }">{{ item.reviewer }}</span>
            </div>
            <div class="footer-item">
              <span class="footer-label">记录：</span>
              <span class="footer-value" :class="{ 'empty-value': !hasDisplayValue(item.recorder) }">{{ item.recorder }}</span>
            </div>
            <div class="footer-item date-item">
              <span class="footer-label">检验日期：</span>
              <span class="footer-value" :class="{ 'empty-value': !hasDisplayValue(item.inspectionDate) }">{{ formatDate(item.inspectionDate) }}</span>
            </div>
          </div>
        </div>
      </template>

      <el-empty v-if="!loading && (!printData || printData.length === 0)" description="暂无打印数据" />
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'printList'
}
</script>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft, Printer, Download, Refresh } from '@element-plus/icons-vue'
import { ParameterSettings } from '@/network/index'
import { useDetailStore } from '@/store/detailStore'
import * as ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

// 类型定义
interface SpecialAttribute {
  title: string
  value: string
}

interface Section {
  name: string
  columnCount: number
  startIndex: number
}

interface SubRow {
  verticalHeader: string
  cells: string[]
}

interface MainRow {
  buildingFloor: string
  axisNumber: string
  subRows: SubRow[]
  // 各轴线的实际值（用于 @@N 替换）
  axisValues: Record<string, string>
}

// 扁平化的表格行（用于分页计算）
interface FlatTableRow {
  mainIndex: number
  subIndex: number
  isFirstSubRow: boolean
  subRowCount: number
  buildingFloor: string
  axisNumber: string
  verticalHeader: string
  cells: string[]
}

interface TableInfo {
  horizontalHeaders: string[]
  sections: Section[]
  mainRows: MainRow[]
  columnBorders: boolean[]
}

// 分页后的打印数据
interface PrintPageData {
  recordNumber: string
  deviceNumber: string
  temperature: string
  reviewer: string
  recorder: string
  inspector: string
  calculator: string
  showCalculator: boolean
  inspectionDate: string
  implementDate: string
  projectName: string
  controlNumber: string
  componentType: string
  specialAttributes: SpecialAttribute[]
  mainTableSingleAttrs: SpecialAttribute[][]
  tableInfo: TableInfo | null
  // 分页相关
  isFirstPage: boolean
  pageRows: FlatTableRow[]
  sections: Section[]
  // 原始数据索引（用于计算总页数）
  dataIndex: number
  pageIndexInData: number
  totalPagesInData: number
  // 动态行高（用于填充空白区域）
  extraRowHeight: number
  // 每个数据行的动态调整高度，正数用于撑开，负数用于横向同页压缩
  rowExtraHeights: number[]
  // 单页方向（由数据中的方向字段决定）
  isLandscape: boolean
}

// 每页最大行数（根据A4纸高度估算，首页和续页不同）
// 竖向：A4纸高度297mm，减去页眉(约50mm)、页脚(约30mm)、边距(20mm)，表格区域约197mm
// 横向：A4纸高度210mm，减去页眉(约50mm)、页脚(约30mm)、边距(20mm)，表格区域约110mm
// 表头行高约10mm，数据行高约6mm（紧凑模式）

// 竖向模式行数（使用紧凑行高后可容纳更多行）
const MAX_ROWS_FIRST_PAGE_PORTRAIT = 23
const MAX_ROWS_CONTINUE_PAGE_PORTRAIT = 25

// 横向模式行数
const MAX_ROWS_FIRST_PAGE_LANDSCAPE = 23
const MAX_ROWS_CONTINUE_PAGE_LANDSCAPE = 25

// 基础行高（单位：px，竖向和横向统一使用紧凑行高）
const BASE_ROW_HEIGHT = 16
const LANDSCAPE_BASE_ROW_HEIGHT = 16
// 横向空间不足时允许在同页压缩行高，避免末行被固定高度裁掉。
const LANDSCAPE_MIN_ROW_HEIGHT = 14
const SINGLE_ATTR_ROW_WEIGHT = 0.35
// 动态撑开时保留安全余量，横向打印额外预留空间，避免像素取整裁掉末行和底部边框。
const PAGE_FILL_SAFETY_PX = 3
const LANDSCAPE_PAGE_FILL_SAFETY_PX = 10

const router = useRouter()
const route = useRoute()
const detailStore = useDetailStore()

// 根据打印方向获取每页最大行数
const getMaxRowsFirstPage = (isLandscape: boolean) => isLandscape ? MAX_ROWS_FIRST_PAGE_LANDSCAPE : MAX_ROWS_FIRST_PAGE_PORTRAIT
const getMaxRowsContinuePage = (isLandscape: boolean) => isLandscape ? MAX_ROWS_CONTINUE_PAGE_LANDSCAPE : MAX_ROWS_CONTINUE_PAGE_PORTRAIT

const getSingleAttrRowWeight = (attrs?: SpecialAttribute[]): number => {
  if (!attrs || attrs.length === 0) return 0
  return Math.ceil(attrs.length / 4) * SINGLE_ATTR_ROW_WEIGHT
}

const loading = ref(false)
const printContentRef = ref<HTMLElement | null>(null)
const printData = ref<PrintPageData[]>([])
let fillMeasureToken = 0

// 计算行高样式（应用到tr上）
const getRowStyle = (extraRowHeight: number, isLandscape: boolean): Record<string, string> => {
  const baseRowHeight = isLandscape ? LANDSCAPE_BASE_ROW_HEIGHT : BASE_ROW_HEIGHT
  const minRowHeight = isLandscape ? LANDSCAPE_MIN_ROW_HEIGHT : BASE_ROW_HEIGHT
  const totalHeight = Math.max(baseRowHeight + extraRowHeight, minRowHeight)
  return {
    height: `${totalHeight}px`
  }
}

// 计算单元格高度样式（应用到td上，确保高度生效）
const getCellStyle = (extraRowHeight: number, isLandscape: boolean): Record<string, string> => {
  const baseRowHeight = isLandscape ? LANDSCAPE_BASE_ROW_HEIGHT : BASE_ROW_HEIGHT
  const minRowHeight = isLandscape ? LANDSCAPE_MIN_ROW_HEIGHT : BASE_ROW_HEIGHT
  const totalHeight = Math.max(baseRowHeight + extraRowHeight, minRowHeight)
  if (extraRowHeight !== 0) {
    return {
      height: `${totalHeight}px`,
      minHeight: `${totalHeight}px`,
      boxSizing: 'border-box'
    }
  }
  return {}
}

const waitForFrame = (): Promise<void> => new Promise(resolve => {
  requestAnimationFrame(() => resolve())
})

const waitForLayoutStable = async () => {
  await nextTick()
  await waitForFrame()
  await waitForFrame()
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready
    } catch {
      // ignore font loading errors and continue with best available layout
    }
  }
}

const applyMeasuredPageFill = async () => {
  if (!printData.value.length) return

  const token = ++fillMeasureToken

  // 第一步：重置所有填充，以紧凑模式渲染
  let pages = printData.value.map(page => ({
    ...page,
    extraRowHeight: 0,
    rowExtraHeights: page.pageRows.map(() => 0)
  }))

  // 多轮测量是必要的：表格的 border-collapse 和 rowspan 会让实际高度
  // 与 CSS 设置值存在细小差异，需要逐轮正向撑开或反向压缩。
  for (let round = 0; round < 4; round++) {
    printData.value = pages
    await waitForLayoutStable()

    if (token !== fillMeasureToken) return

    const pageEls = Array.from(
      printContentRef.value?.querySelectorAll<HTMLElement>('.print-content') || []
    )
    let changed = false

    pages = pages.map((page, pageIndex) => {
      const pageEl = pageEls[pageIndex]
      const tableEl = pageEl?.querySelector<HTMLElement>('.data-table')
      const footerEl = pageEl?.querySelector<HTMLElement>('.page-footer')

      if (!tableEl || !footerEl || page.pageRows.length === 0) {
        return page
      }

      // 直接测量表格底部到页脚顶部的间距，避免通过页面高度反推时
      // 把 flex 容器的内部空余高度重复计算进去。
      const tableRect = tableEl.getBoundingClientRect()
      const footerRect = footerEl.getBoundingClientRect()
      const fillSafety = page.isLandscape ? LANDSCAPE_PAGE_FILL_SAFETY_PX : PAGE_FILL_SAFETY_PX
      const remainingGap = Math.floor(footerRect.top - tableRect.bottom - fillSafety)

      const rowCount = page.pageRows.length
      const currentHeights = page.rowExtraHeights?.length === rowCount
        ? page.rowExtraHeights
        : page.pageRows.map(() => 0)
      let rowExtraHeights: number[]

      if (remainingGap > 0) {
        const fillPerRow = Math.floor(remainingGap / rowCount)
        const remainder = remainingGap % rowCount
        rowExtraHeights = currentHeights.map((height, index) =>
          height + fillPerRow + (index < remainder ? 1 : 0)
        )
      } else if (remainingGap < 0 && page.isLandscape) {
        // 横向页超出安全区时反向压缩，优先在同一页完整保留末行和底部边框。
        const minExtraHeight = LANDSCAPE_MIN_ROW_HEIGHT - LANDSCAPE_BASE_ROW_HEIGHT
        const reducibleHeight = currentHeights.reduce(
          (total, height) => total + Math.max(0, height - minExtraHeight),
          0
        )
        const reduction = Math.min(-remainingGap, reducibleHeight)
        const reducePerRow = Math.floor(reduction / rowCount)
        const remainder = reduction % rowCount
        rowExtraHeights = currentHeights.map((height, index) =>
          Math.max(minExtraHeight, height - reducePerRow - (index < remainder ? 1 : 0))
        )
      } else {
        return page
      }

      const hasChanged = rowExtraHeights.some((height, index) => height !== currentHeights[index])
      if (!hasChanged) {
        return page
      }

      changed = true
      return {
        ...page,
        extraRowHeight: Math.max(...rowExtraHeights, 0),
        rowExtraHeights
      }
    })

    if (!changed) break
  }

  if (token !== fillMeasureToken) return

  printData.value = pages
  await waitForLayoutStable()

  // 重新获取应用动态行高后的节点，避免使用填充前的旧 DOM 尺寸进行验证。
  const finalPageEls = Array.from(
    printContentRef.value?.querySelectorAll<HTMLElement>('.print-content') || []
  )
  pages.forEach((page, pageIndex) => {
    const pageEl = finalPageEls[pageIndex]
    const tableEl = pageEl?.querySelector<HTMLElement>('.data-table')
    const footerEl = pageEl?.querySelector<HTMLElement>('.page-footer')

    if (!tableEl || !footerEl) return

    const finalGap = footerEl.getBoundingClientRect().top - tableEl.getBoundingClientRect().bottom
    if (finalGap < -1) {
      console.error(`[页面${pageIndex + 1}] 行高调整后仍溢出 ${Math.abs(finalGap).toFixed(0)}px`)
    }
  })
}

const hasDisplayValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  return true
}

// 加载打印数据
const loadPrintData = async () => {
  loading.value = true
  try {
    const cacheKey = typeof route.query.cacheKey === 'string' ? route.query.cacheKey : ''
    const rawData = cacheKey
      ? detailStore.getPrintListData(cacheKey)
      : detailStore.getLatestPrintListData()

    if (rawData && rawData.length > 0) {
      // 使用新的渲染式分页
      const allPages: PrintPageData[] = []
      for (const item of rawData) {
        const pages = await parsePrintItemWithRenderPagination(item, allPages.length)
        allPages.push(...pages)
      }
      printData.value = allPages
      await applyMeasuredPageFill()
    } else {
      printData.value = []
      ElMessage.warning('暂无可用的打印数据，请重新获取')
    }
  } finally {
    loading.value = false
  }
}

// 新的渲染式分页：先渲染后分页
const parsePrintItemWithRenderPagination = async (item: {
  方向: boolean;
  记录编号: string;
  设备编号: string;
  环境温度: string;
  校核: string;
  记录: string;
  检验: string;
  计算: string;
  检验日期: string;
  检测原始数据检测项目: Record<string, unknown>;
}, dataIndexOffset: number): Promise<PrintPageData[]> => {
  const project = item.检测原始数据检测项目 || {}
  const formatDesc = (project.检测原始数据检测数据格式描述 as Record<string, unknown>) || {}

  const isLandscape = item.方向 === true

  const detailDataList =
    (project.检测原始数据详细数据主表集合 as Array<Record<string, unknown>>) || []
  const mainTableSingleAttrs: SpecialAttribute[][] = detailDataList.map((detailData) =>
    parseSpecialAttributes(
      (detailData.检测原始数据检测项目单条数据特殊属性集合 as Array<Record<string, unknown>>) || []
    )
  )

  const baseData = {
    recordNumber: item.记录编号 || '',
    deviceNumber: item.设备编号 || '',
    temperature: item.环境温度 || '',
    reviewer: item.校核 || '',
    recorder: item.记录 || '',
    inspector: item.检验 || '',
    calculator: item.计算 || '',
    showCalculator: Boolean(item.计算) || Boolean(project.是否计算),
    inspectionDate: item.检验日期 || '',
    implementDate: (project.实施日期 as string) || '',
    projectName: (project.名称 as string) || '',
    controlNumber: (project.受控编号 as string) || '',
    componentType: (project.检测对象类型 as string) || '',
    specialAttributes: parseSpecialAttributes(
      (project.检测原始数据检测项目特殊属性集合 as Array<Record<string, unknown>>) || []
    ),
    mainTableSingleAttrs,
    dataIndex: dataIndexOffset,
    isLandscape
  }

  const tableInfo = parseTableInfo(formatDesc, project)

  if (!tableInfo || tableInfo.mainRows.length === 0) {
    return [{
      ...baseData,
      tableInfo,
      isFirstPage: true,
      pageRows: [],
      sections: tableInfo?.sections || [],
      pageIndexInData: 0,
      totalPagesInData: 1,
      extraRowHeight: 0,
      rowExtraHeights: []
    }]
  }

  // 将表格数据扁平化为行
  const flatRows: FlatTableRow[] = []
  tableInfo.mainRows.forEach((mainRow, mainIndex) => {
    const singleAttrs = mainTableSingleAttrs[mainIndex] || []
    const subRows = mainRow.subRows.length > 0
      ? mainRow.subRows
      : singleAttrs.length > 0
        ? [{ verticalHeader: '', cells: tableInfo.horizontalHeaders.map(() => '') }]
        : []

    subRows.forEach((subRow, subIndex) => {
      flatRows.push({
        mainIndex,
        subIndex,
        isFirstSubRow: subIndex === 0,
        subRowCount: subRows.length,
        buildingFloor: mainRow.buildingFloor,
        axisNumber: mainRow.axisNumber,
        verticalHeader: subRow.verticalHeader,
        cells: subRow.cells
      })
    })
  })

  console.log(`[渲染式分页] 开始处理，共${flatRows.length}行数据，方向: ${isLandscape ? '横向' : '竖向'}`)

  // 渲染式分页：逐步添加行并测量
  const pages: PrintPageData[] = []
  let currentPageRows: FlatTableRow[] = []
  let currentIndex = 0
  let pageIndexInData = 0

  while (currentIndex < flatRows.length) {
    const currentRow = flatRows[currentIndex]
    const isFirstPage = pageIndexInData === 0

    // 尝试添加这个主表的所有行
    if (currentRow.isFirstSubRow) {
      const tempRows = [...currentPageRows]
      const mainStartIndex = currentIndex
      const mainEndIndex = currentIndex + currentRow.subRowCount

      // 收集主表的所有子行
      for (let j = mainStartIndex; j < mainEndIndex && j < flatRows.length; j++) {
        tempRows.push(flatRows[j])
      }

      // 创建临时页面数据进行测量
      const tempPage: PrintPageData = {
        ...baseData,
        tableInfo,
        isFirstPage,
        pageRows: tempRows,
        sections: tableInfo.sections,
        pageIndexInData,
        totalPagesInData: 0,
        extraRowHeight: 0,
        rowExtraHeights: tempRows.map(() => 0),
        isLandscape
      }

      // 渲染并测量是否溢出
      printData.value = [tempPage]
      await waitForLayoutStable()

      let isOverflow = await checkPageOverflow(0)

      if (isOverflow && isLandscape) {
        // 分页前先按最低行高实测，能同页容纳时不把主表移到下一页。
        printData.value = [{
          ...tempPage,
          rowExtraHeights: tempRows.map(() => LANDSCAPE_MIN_ROW_HEIGHT - LANDSCAPE_BASE_ROW_HEIGHT)
        }]
        await waitForLayoutStable()
        isOverflow = await checkPageOverflow(0)
      }

      if (isOverflow && currentPageRows.length > 0) {
        // 溢出且当前页已有内容 -> 完成当前页，主表放到下一页
        console.log(`[渲染式分页] 页面${pageIndexInData + 1}完成，共${currentPageRows.length}行`)
        pages.push({
          ...baseData,
          tableInfo,
          isFirstPage,
          pageRows: currentPageRows,
          sections: tableInfo.sections,
          pageIndexInData,
          totalPagesInData: 0,
          extraRowHeight: 0,
          rowExtraHeights: currentPageRows.map(() => 0),
          isLandscape
        })

        // 开始新页
        currentPageRows = []
        pageIndexInData++
      } else {
        // 不溢出，或者当前页为空（强制放入）-> 接受这个主表
        currentPageRows = tempRows
        currentIndex = mainEndIndex
      }
    } else {
      // 不应该出现
      console.warn(`[渲染式分页] 遇到非首行`)
      currentIndex++
    }
  }

  // 添加最后一页
  if (currentPageRows.length > 0) {
    console.log(`[渲染式分页] 页面${pageIndexInData + 1}完成（最后一页），共${currentPageRows.length}行`)
    pages.push({
      ...baseData,
      tableInfo,
      isFirstPage: pageIndexInData === 0,
      pageRows: currentPageRows,
      sections: tableInfo.sections,
      pageIndexInData,
      totalPagesInData: 0,
      extraRowHeight: 0,
      rowExtraHeights: currentPageRows.map(() => 0),
      isLandscape
    })
  }

  // 更新总页数
  pages.forEach(page => {
    page.totalPagesInData = pages.length
  })

  console.log(`[渲染式分页] 完成，共${pages.length}页`)

  return pages
}

// 检查页面是否溢出
const checkPageOverflow = async (pageIndex: number): Promise<boolean> => {
  const pageEls = Array.from(
    printContentRef.value?.querySelectorAll<HTMLElement>('.print-content') || []
  )

  const pageEl = pageEls[pageIndex]
  if (!pageEl) return false

  const tableContainerEl = pageEl.querySelector<HTMLElement>('.table-container')
  const footerEl = pageEl.querySelector<HTMLElement>('.page-footer')

  if (!tableContainerEl || !footerEl) return false

  const pageRect = pageEl.getBoundingClientRect()
  const tableContainerRect = tableContainerEl.getBoundingClientRect()
  const footerRect = footerEl.getBoundingClientRect()

  const headerContentHeight = tableContainerRect.top - pageRect.top
  const footerHeight = pageRect.bottom - footerRect.top
  const tableAvailableHeight = pageRect.height - headerContentHeight - footerHeight

  const tableEl = pageEl.querySelector<HTMLElement>('.data-table')
  if (!tableEl) return false

  const tableRect = tableEl.getBoundingClientRect()
  const currentTableHeight = tableRect.height

  // 只留10px安全边距
  const isOverflow = currentTableHeight > (tableAvailableHeight - 10)

  if (isOverflow) {
    console.log(`[溢出检测] 页面${pageIndex + 1}: 表格${currentTableHeight.toFixed(0)}px > 可用${(tableAvailableHeight - 10).toFixed(0)}px`)
  }

  return isOverflow
}

// 解析单条打印数据并分页（旧方法，保留作为备用）
const parsePrintItemWithPagination = (item: {
  方向: boolean;
  记录编号: string;
  设备编号: string;
  环境温度: string;
  校核: string;
  记录: string;
  检验: string;
  计算: string;
  检验日期: string;
  检测原始数据检测项目: Record<string, unknown>;
}, dataIndex: number): PrintPageData[] => {
  const project = item.检测原始数据检测项目 || {}
  const formatDesc = (project.检测原始数据检测数据格式描述 as Record<string, unknown>) || {}
  
  // 从项目顶层获取方向字段：true=横向，false=竖向
  const isLandscape = item.方向 === true

  const detailDataList =
    (project.检测原始数据详细数据主表集合 as Array<Record<string, unknown>>) || []
  const mainTableSingleAttrs: SpecialAttribute[][] = detailDataList.map((detailData) =>
    parseSpecialAttributes(
      (detailData.检测原始数据检测项目单条数据特殊属性集合 as Array<Record<string, unknown>>) || []
    )
  )

  const baseData = {
    recordNumber: item.记录编号 || '',
    deviceNumber: item.设备编号 || '',
    temperature: item.环境温度 || '',
    reviewer: item.校核 || '',
    recorder: item.记录 || '',
    inspector: item.检验 || '',
    calculator: item.计算 || '',
    showCalculator: Boolean(item.计算) || Boolean(project.是否计算),
    inspectionDate: item.检验日期 || '',
    implementDate: (project.实施日期 as string) || '',
    projectName: (project.名称 as string) || '',
    controlNumber: (project.受控编号 as string) || '',
    componentType: (project.检测对象类型 as string) || '',
    specialAttributes: parseSpecialAttributes(
      (project.检测原始数据检测项目特殊属性集合 as Array<Record<string, unknown>>) || []
    ),
    mainTableSingleAttrs,
    dataIndex,
    isLandscape
  }

  const tableInfo = parseTableInfo(formatDesc, project)
  
  if (!tableInfo || tableInfo.mainRows.length === 0) {
    // 没有表格数据，返回单页
    return [{
      ...baseData,
      tableInfo,
      isFirstPage: true,
      pageRows: [],
      sections: tableInfo?.sections || [],
      pageIndexInData: 0,
      totalPagesInData: 1,
      extraRowHeight: 0,
      rowExtraHeights: []
    }]
  }

  // 将表格数据扁平化为行
  const flatRows: FlatTableRow[] = []
  tableInfo.mainRows.forEach((mainRow, mainIndex) => {
    const singleAttrs = mainTableSingleAttrs[mainIndex] || []
    const subRows = mainRow.subRows.length > 0
      ? mainRow.subRows
      : singleAttrs.length > 0
        ? [{ verticalHeader: '', cells: tableInfo.horizontalHeaders.map(() => '') }]
        : []

    subRows.forEach((subRow, subIndex) => {
      flatRows.push({
        mainIndex,
        subIndex,
        isFirstSubRow: subIndex === 0,
        subRowCount: subRows.length,
        buildingFloor: mainRow.buildingFloor,
        axisNumber: mainRow.axisNumber,
        verticalHeader: subRow.verticalHeader,
        cells: subRow.cells
      })
    })
  })

  // 按行数分页，确保主表不被截断
  // 动态计算表头占用的等效行数（考虑列数多时表头可能换行导致高度增加）
  const calculateHeaderRowEquivalent = (horizontalHeaders: string[], isLandscape: boolean): number => {
    const colCount = horizontalHeaders.length

    let headerRowEquivalent = 1

    // 根据列数和打印方向调整
    if (isLandscape) {
      // 横向打印：纸张高度有限，但宽度充足，表头换行相对较少
      if (colCount > 25) {
        headerRowEquivalent = 2.5    // 列数很多，表头很高
      } else if (colCount > 20) {
        headerRowEquivalent = 2
      } else if (colCount > 15) {
        headerRowEquivalent = 1.8
      } else if (colCount > 10) {
        headerRowEquivalent = 1.5
      } else if (colCount > 5) {
        headerRowEquivalent = 1.3
      } else {
        headerRowEquivalent = 1
      }
    } else {
      // 竖向打印：纸张宽度有限，表头换行较多
      if (colCount > 25) {
        headerRowEquivalent = 2
      } else if (colCount > 20) {
        headerRowEquivalent = 1.8
      } else if (colCount > 15) {
        headerRowEquivalent = 1.6
      } else if (colCount > 10) {
        headerRowEquivalent = 1.4
      } else if (colCount > 5) {
        headerRowEquivalent = 1.2
      } else {
        headerRowEquivalent = 1
      }
    }

    // 调试日志
    console.log(`[分页计算] 列数: ${colCount}, 方向: ${isLandscape ? '横向' : '竖向'}, 表头等效行数: ${headerRowEquivalent.toFixed(1)}`)

    return headerRowEquivalent
  }

  const HEADER_ROW_EQUIVALENT = calculateHeaderRowEquivalent(tableInfo.horizontalHeaders, isLandscape)
  const pages: PrintPageData[] = []
  let currentIndex = 0
  let pageIndexInData = 0

  while (currentIndex < flatRows.length) {
    const isFirstPage = pageIndexInData === 0
    const maxRows = isFirstPage ? getMaxRowsFirstPage(isLandscape) : getMaxRowsContinuePage(isLandscape)
    // 实际可用的数据行数（减去表头等效行数）
    const availableRows = Math.max(1, Math.floor(maxRows - HEADER_ROW_EQUIVALENT))

    // 调试日志
    if (currentIndex === 0) {
      console.log(`[分页计算] 每页最大行数: ${maxRows}, 表头等效: ${HEADER_ROW_EQUIVALENT.toFixed(1)}, 可用数据行: ${availableRows}`)
    }
    
    const pageRows: FlatTableRow[] = []
    let rowsInPage = 0
    let i = currentIndex

    while (i < flatRows.length) {
      const currentRow = flatRows[i]

      // 如果是主表的第一行，需要检查整个主表
      if (currentRow.isFirstSubRow) {
        const singleAttrWeight = getSingleAttrRowWeight(mainTableSingleAttrs[currentRow.mainIndex])
        const mainRowCount = currentRow.subRowCount + singleAttrWeight
        const remainingSpace = availableRows - rowsInPage

        // 如果整个主表放不下
        if (mainRowCount > remainingSpace) {
          // 如果当前页已经有超过50%的内容，断页，将主表放到下一页
          if (rowsInPage > Math.floor(availableRows * 0.5)) {
            break
          }
          // 否则，即使超出也强制放入（避免页面太空）
        }

        // 将整个主表的所有子行一次性添加
        const mainStartIndex = i
        const mainEndIndex = i + currentRow.subRowCount

        for (let j = mainStartIndex; j < mainEndIndex && j < flatRows.length; j++) {
          pageRows.push(flatRows[j])
        }

        rowsInPage += mainRowCount
        i = mainEndIndex

        // 添加调试日志
        console.log(`[分页] 主表${currentRow.mainIndex + 1}: ${currentRow.subRowCount}子行 + ${singleAttrWeight}特殊属性 = ${mainRowCount}行, 当前页累计: ${rowsInPage}/${availableRows}`)
      } else {
        // 理论上不应该走到这里，因为所有行都应该有isFirstSubRow标记
        console.warn(`[分页警告] 遇到非首行: mainIndex=${currentRow.mainIndex}, subIndex=${currentRow.subIndex}`)
        pageRows.push(currentRow)
        rowsInPage++
        i++
      }

      // 安全终止：如果累计行数已经超过可用行数，停止添加
      if (rowsInPage >= availableRows) {
        console.log(`[分页] 页面已满 (${rowsInPage}/${availableRows})，结束当前页`)
        break
      }
    }
    
    pages.push({
      ...baseData,
      tableInfo,
      isFirstPage,
      pageRows,
      sections: tableInfo.sections,
      pageIndexInData,
      totalPagesInData: 0, // 稍后计算
      extraRowHeight: 0,
      rowExtraHeights: pageRows.map(() => 0),
      isLandscape
    })

    currentIndex = i
    pageIndexInData++
  }

  // 更新总页数；行高填充在 DOM 渲染后按实际剩余空间计算。
  pages.forEach(page => {
    page.totalPagesInData = pages.length
  })

  return pages
}

// 解析特殊属性
const parseSpecialAttributes = (attrList: Array<Record<string, unknown>>): SpecialAttribute[] => {
  if (!attrList || !Array.isArray(attrList)) return []
  
  // 排除已在表头显示的属性
  const excludeAttrs = ['环境温度', '设备编号']
  
  return attrList
    .filter(attr => {
      const title = (attr.标题 as string) || ''
      return title && !excludeAttrs.includes(title)
    })
    .map(attr => ({
      title: (attr.标题 as string) || '',
      value: (attr.内容 as string) || ''
    }))
}

// 解析表格信息
const parseTableInfo = (
  formatDesc: Record<string, unknown>,
  project: Record<string, unknown>
): TableInfo | null => {
  const horizontalHeaders = (formatDesc.横向标题 as string[]) || []
  const columnBorders = (formatDesc.分区线 as boolean[]) || []

  if (horizontalHeaders.length === 0) return null

  // 解析分区标题
  let sectionNames: string[] = []
  try {
    const sectionTitleJSON = formatDesc.分区标题JSON as string
    if (sectionTitleJSON) {
      sectionNames = JSON.parse(sectionTitleJSON)
    }
  } catch {
    sectionNames = []
  }

  // 构建分区信息
  const sections = parseSections(horizontalHeaders, columnBorders, sectionNames)

  // 获取详细数据主表集合 - 每个主表对应一个大行
  const detailDataList =
    (project.检测原始数据详细数据主表集合 as Array<Record<string, unknown>>) || []

  // 构建主行数据
  const mainRows: MainRow[] = detailDataList.map(
    (detailData: Record<string, unknown>) => {
      // 楼号楼层：建筑名称 + 层数 + "层"，如 "清芬1层"
      const floor = (detailData.层数 as string | number) || ''
      const buildingName = (detailData.建筑名称 as string) || ''
      const buildingFloor = buildingName
        ? `${buildingName}${floor}层`
        : floor
          ? `${floor}层`
          : ''

      // 轴线号：从显示摘要中提取 "轴线：[xxx]"
      const summary = (detailData.显示摘要 as string) || ''
      let axisNumber = ''
      const axisMatch = summary.match(/轴线[：:]\s*\[([^\]]*)\]/)
      if (axisMatch) {
        axisNumber = axisMatch[1]
      }

      // 各轴线值（用于 @@N 替换）：保留原始 轴线1~轴线4 字段
      const axisValues: Record<string, string> = {
        '1': String((detailData['轴线1'] as string) ?? ''),
        '2': String((detailData['轴线2'] as string) ?? ''),
        '3': String((detailData['轴线3'] as string) ?? ''),
        '4': String((detailData['轴线4'] as string) ?? '')
      }

      // 获取子表数据 - 每个子表对应一个小行
      const subDataList =
        (detailData.检测原始数据详细数据子表集合 as Array<Record<string, unknown>>) || []

      const subRows: SubRow[] = subDataList.map(
        (subData: Record<string, unknown>) => {
          // 纵向标题（去掉@@前缀）
          let verticalHeader = (subData.Header as string) || ''
          if (verticalHeader.startsWith('@@')) {
            verticalHeader = verticalHeader.substring(2)
          }

          // 构建单元格数据
          const cells: string[] = horizontalHeaders.map(
            (_: string, colIndex: number) => {
              const key = `_${colIndex + 1}`
              const value = subData[key]
              if (value === undefined || value === null || value === '') {
                return ''
              }
              // 布尔值转换：true -> 是，false -> 否
              if (value === true || value === 'true' || value === 'True') {
                return '是'
              } else if (value === false || value === 'false' || value === 'False') {
                return '否'
              }
              return String(value)
            }
          )

          return {
            verticalHeader,
            cells
          }
        }
      )

      return {
        buildingFloor,
        axisNumber,
        subRows,
        axisValues
      }
    }
  )

  return {
    horizontalHeaders,
    sections,
    mainRows,
    columnBorders
  }
}

const parseSections = (
  horizontalHeaders: string[],
  columnBorders: boolean[],
  sectionNames: string[]
): Section[] => {
  const sections: Section[] = []

  if (horizontalHeaders.length === 0) return sections

  // 判断数组格式：
  // - 如果长度 > 横向标题数量，第一个元素是第一分区名称
  // - 如果长度 = 横向标题数量，没有第一分区名称（旧格式兼容）
  const hasFirstSectionSlot = sectionNames.length > horizontalHeaders.length

  let currentStart = 0
  // 第一个分区的名称
  let currentSectionName = hasFirstSectionSlot ? (sectionNames[0] || '') : ''

  for (let i = 0; i < horizontalHeaders.length; i++) {
    // 检查是否需要结束当前分区（在分区线位置或最后一列）
    const isLastColumn = i === horizontalHeaders.length - 1
    const hasBorder = columnBorders[i] === true

    if (hasBorder || isLastColumn) {
      const columnCount = i - currentStart + 1
      sections.push({
        name: currentSectionName,
        columnCount,
        startIndex: currentStart
      })

      // 准备下一个分区
      if (!isLastColumn) {
        currentStart = i + 1
        // 下一个分区的名称：sectionNames[i+1] 是第 i 列右边分区线后的分区名称
        // 如果有第一分区槽位，索引为 i+1；否则为 i（旧格式兼容）
        const nextNameIndex = hasFirstSectionSlot ? i + 1 : i
        currentSectionName = sectionNames[nextNameIndex] || ''
      }
    }
  }

  return sections
}

// 获取单元格的分区样式类（基于 sections 数组）
const getSectionClassByIndex = (sections: Section[], colIndex: number): string => {
  for (const section of sections) {
    if (section.startIndex === colIndex && colIndex > 0) {
      return 'section-start'
    }
  }
  return ''
}

// 判断当前行是否是该主表在当前页的第一行
const isFirstRowOfMainInPage = (pageRows: FlatTableRow[], rowIndex: number): boolean => {
  const currentRow = pageRows[rowIndex]
  if (rowIndex === 0) return true
  const prevRow = pageRows[rowIndex - 1]
  return prevRow.mainIndex !== currentRow.mainIndex
}

// 转换横向标题：处理 @@1~4 替换为对应轴线值（与详情页逻辑一致）
const getTransformedTitle = (title: string, tableInfo: TableInfo | null, firstRow?: FlatTableRow): string => {
  const match = title.match(/@@([1-4])/)
  if (!match) return title
  const axisNumber = match[1]

  // 优先从对应主表的 axisValues 取（来自原始主表的 轴线1~轴线4）
  const mainRow = firstRow ? tableInfo?.mainRows?.[firstRow.mainIndex] : undefined
  const axisValue = mainRow?.axisValues?.[axisNumber] || ''

  // 如果轴线值为空或 "0"，保留原标题
  if (!axisValue || axisValue === '0') {
    return title
  }
  return title.replace(/@@([1-4])/, axisValue)
}

// 判断横向标题对应列是否有分区线
const hasDividerLine = (tableInfo: TableInfo | null, index: number): boolean => {
  if (!tableInfo || !Array.isArray(tableInfo.columnBorders)) return false
  return tableInfo.columnBorders[index] === true
}

const getTableColumnCount = (page: PrintPageData): number => {
  return 4 + (page.tableInfo?.horizontalHeaders?.length || 0)
}

// 获取指定主表的单条数据特殊属性列表
const getMainSingleAttrsList = (page: PrintPageData, mainIndex: number): SpecialAttribute[] => {
  return page.mainTableSingleAttrs?.[mainIndex] || []
}

// 需要显示的项目级特殊属性：过滤掉标题为"检测对象"且值为"默认"的项
const visibleSpecialAttributes = (page: PrintPageData): SpecialAttribute[] => {
  return (page.specialAttributes || []).filter((attr) => {
    if (attr.title === '检测对象' && String(attr.value ?? '').trim() === '默认') {
      return false
    }
    return true
  })
}

const hasMainSingleAttrs = (page: PrintPageData, mainIndex: number): boolean => {
  return getMainSingleAttrsList(page, mainIndex).length > 0
}

const shouldRenderSingleAttrsRow = (page: PrintPageData, row: FlatTableRow, rowIndex: number): boolean => {
  return row.isFirstSubRow && isFirstRowOfMainInPage(page.pageRows, rowIndex) && hasMainSingleAttrs(page, row.mainIndex)
}

const shouldRenderSeqCell = (page: PrintPageData, row: FlatTableRow, rowIndex: number): boolean => {
  return row.isFirstSubRow && isFirstRowOfMainInPage(page.pageRows, rowIndex) && !hasMainSingleAttrs(page, row.mainIndex)
}

const getSeqRowspanInPage = (page: PrintPageData, rowIndex: number): number => {
  const row = page.pageRows[rowIndex]
  return getRowspanInPage(page.pageRows, rowIndex) + (hasMainSingleAttrs(page, row.mainIndex) ? 1 : 0)
}

const formatSingleAttrsText = (attrs: SpecialAttribute[]): string => {
  return attrs.map(attr => `${attr.title}：${attr.value}`).join('  ')
}

// 计算当前主表在当前页的 rowspan
const getRowspanInPage = (pageRows: FlatTableRow[], rowIndex: number): number => {
  const currentRow = pageRows[rowIndex]
  let count = 1
  for (let i = rowIndex + 1; i < pageRows.length; i++) {
    if (pageRows[i].mainIndex === currentRow.mainIndex) {
      count++
    } else {
      break
    }
  }
  return count
}

// 格式化日期代码（如 20250711）
const formatDateCode = (dateStr: string): string => {
  if (!dateStr || dateStr.startsWith('0001') || dateStr.startsWith('1900')) return ''
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  } catch {
    return ''
  }
}

// 格式化日期（如 2025年7月11日）
const formatDate = (dateStr: string): string => {
  if (!dateStr || dateStr.startsWith('0001') || dateStr.startsWith('1900')) {
    return '____年__月__日'
  }
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return '____年__月__日'
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
  } catch {
    return '____年__月__日'
  }
}

// 返回上一页
const goBack = () => {
  router.go(-1)
}

const handleRefreshPrintData = async () => {
  const projectId = typeof route.query.id === 'string' ? route.query.id : ''
  if (!projectId) {
    ElMessage.warning('缺少项目ID，无法刷新打印数据')
    return
  }

  loading.value = true
  try {
    const res = await ParameterSettings.operatingProjectPrint({ id: projectId })
    if (!res?.success || !Array.isArray(res.response)) {
      ElMessage.error(res?.msg || '刷新打印数据失败')
      return
    }

    const cacheKey = typeof route.query.cacheKey === 'string' && route.query.cacheKey
      ? route.query.cacheKey
      : `analysis-print-${projectId}`
    detailStore.setPrintListData(cacheKey, res.response)
    await loadPrintData()
    ElMessage.success('打印数据已刷新')
  } catch (error) {
    console.error('刷新打印数据失败:', error)
    ElMessage.error('刷新打印数据失败')
  } finally {
    loading.value = false
  }
}

// 打印功能 - 支持混合方向
const handlePrint = async () => {
  await applyMeasuredPageFill()
  await nextTick()

  const printContent = printContentRef.value
  if (!printContent) {
    ElMessage.warning('打印内容未加载')
    return
  }

  // 检查是否有混合方向
  const hasLandscape = printData.value.some(p => p.isLandscape)
  const hasPortrait = printData.value.some(p => !p.isLandscape)
  const isMixedOrientation = hasLandscape && hasPortrait
  
  // 混合方向默认使用竖向纸张，横向内容保持90度旋转展示
  const pageOrientation = hasLandscape && !hasPortrait ? 'landscape' : 'portrait'

  // 创建隐藏的iframe
  const iframe = document.createElement('iframe')
  iframe.style.position = 'absolute'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = 'none'
  iframe.style.left = '-9999px'
  document.body.appendChild(iframe)

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
  if (!iframeDoc) {
    document.body.removeChild(iframe)
    ElMessage.error('打印初始化失败')
    return
  }

  // 获取当前页面的样式
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((el) => el.outerHTML)
    .join('')

  // 只调整打印克隆：每个旋转页使用独立的竖向分页容器。
  const printContentClone = printContent.cloneNode(true) as HTMLElement
  if (isMixedOrientation) {
    const landscapePages = Array.from(
      printContentClone.querySelectorAll<HTMLElement>('.print-content.landscape-content')
    )
    landscapePages.forEach(pageEl => {
      const parent = pageEl.parentNode
      if (!parent) return

      const shell = document.createElement('div')
      shell.className = 'landscape-print-shell'
      parent.replaceChild(shell, pageEl)
      shell.appendChild(pageEl)
    })
  }

  const landscapeRotateStyle = isMixedOrientation ? `
    /* 混合方向统一使用默认竖向 @page，避免命名页重复切换纸张 */
    .print-content,
    .print-content.landscape-content,
    .landscape-print-shell {
      page: auto !important;
    }

    .print-content:not(.landscape-content) {
      transform: none !important;
    }

    /* 外壳参与分页，内部内容保留文档流并旋转，避免连续页重叠 */
    .landscape-print-shell {
      position: relative;
      width: 210mm !important;
      height: 297mm !important;
      min-height: 297mm !important;
      max-height: 297mm !important;
      margin: 0 auto !important;
      overflow: hidden;
      box-sizing: border-box;
      background: #fff;
      page-break-after: always;
      page-break-inside: avoid;
    }

    .landscape-print-shell:last-child {
      page-break-after: avoid;
    }

    .landscape-print-shell > .print-content.landscape-content {
      position: relative !important;
      left: -43.5mm !important;
      top: 43.5mm !important;
      width: 297mm !important;
      height: 210mm !important;
      min-height: 210mm !important;
      max-height: 210mm !important;
      margin: 0 !important;
      transform: rotate(90deg);
      transform-origin: center center;
      page-break-after: auto !important;
      page-break-inside: auto !important;
    }
  ` : ''

  iframeDoc.open()
  iframeDoc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>打印列表</title>
      ${styles}
      <style>
        body { margin: 0; padding: 0; background: #fff; }
        /* 每页独立分页，避免外层 flex 在混合方向时产生定位偏差 */
        .print-wrapper { display: block; padding: 0; }
        
        /* 竖向页面样式 */
        .print-content { 
          box-shadow: none !important; 
          max-width: none !important;
          width: 210mm !important;
          height: 297mm !important;
          min-height: 297mm !important;
          max-height: 297mm !important;
          padding: 10mm !important;
          margin: 0 auto !important;
          page-break-after: always;
          page-break-inside: avoid;
          box-sizing: border-box;
          overflow: hidden;
        }
        
        /* 横向页面样式 */
        .print-content.landscape-content {
          width: 297mm !important;
          height: 210mm !important;
          min-height: 210mm !important;
          max-height: 210mm !important;
        }
        
        .print-content:last-child {
          page-break-after: avoid;
        }
        
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          
          @page {
            size: A4 ${pageOrientation};
            margin: 0;
          }
          
          .print-content {
            width: ${pageOrientation === 'portrait' ? '210mm' : '297mm'} !important;
            height: ${pageOrientation === 'portrait' ? '297mm' : '210mm'} !important;
            min-height: ${pageOrientation === 'portrait' ? '297mm' : '210mm'} !important;
            max-height: ${pageOrientation === 'portrait' ? '297mm' : '210mm'} !important;
          }
          
          ${landscapeRotateStyle}
        }
      </style>
    </head>
    <body>
      ${printContentClone.outerHTML}
    </body>
    </html>
  `)
  iframeDoc.close()

  // 等待iframe加载完成后打印
  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.print()
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1000)
    }, 100)
  }
}

// 导出Excel功能（使用ExcelJS支持完整样式）
const handleExportExcel = async () => {
  if (!printData.value || printData.value.length === 0) {
    ElMessage.warning('暂无数据可导出')
    return
  }

  try {
    const workbook = new ExcelJS.Workbook()
    
    // 为每页创建一个工作表
    for (let pageIndex = 0; pageIndex < printData.value.length; pageIndex++) {
      const pageData = printData.value[pageIndex]
      
      // 工作表名称
      let sheetName: string
      if (printData.value.length === 1) {
        sheetName = '打印列表'
      } else {
        const dataIndex = pageData.dataIndex
        const pageInData = pageData.pageIndexInData + 1
        const totalInData = pageData.totalPagesInData
        
        if (totalInData > 1) {
          sheetName = `数据${dataIndex + 1}_第${pageInData}页`
        } else {
          sheetName = `数据${dataIndex + 1}`
        }
      }
      
      const worksheet = workbook.addWorksheet(sheetName)
      
      // 计算实际需要的列数 - 完全基于表格数据
      // 表格列数 = 4个固定列（序号、楼号楼层、轴线号、纵向标题）+ 横向标题数量
      const tableDataCols = 4 + (pageData.tableInfo?.horizontalHeaders?.length || 0)
      
      let currentRow = 1
      
      // 页眉信息 - 使用表格实际列数
      const headerRow = worksheet.getRow(currentRow++)
      headerRow.getCell(1).value = formatDateCode(pageData.implementDate)
      headerRow.getCell(tableDataCols).value = `实施日期：${formatDate(pageData.implementDate)}`
      headerRow.font = { size: 11 }
      
      // 空行
      currentRow++
      
      // 公司标题
      const companyRow1 = worksheet.getRow(currentRow++)
      companyRow1.getCell(1).value = '建研院检测中心有限公司'
      companyRow1.font = { bold: true, size: 16 }
      companyRow1.alignment = { horizontal: 'center', vertical: 'middle' }
      
      const companyRow2 = worksheet.getRow(currentRow++)
      companyRow2.getCell(1).value = '国家建筑工程质量检验检测中心'
      companyRow2.font = { bold: true, size: 14 }
      companyRow2.alignment = { horizontal: 'center', vertical: 'middle' }
      
      const projectRow = worksheet.getRow(currentRow++)
      projectRow.getCell(1).value = pageData.projectName
      projectRow.font = { bold: true, size: 16 }
      projectRow.alignment = { horizontal: 'center', vertical: 'middle' }
      
      const codeRow = worksheet.getRow(currentRow++)
      codeRow.getCell(1).value = `(${pageData.controlNumber})`
      codeRow.font = { bold: true, size: 14 }
      codeRow.alignment = { horizontal: 'center', vertical: 'middle' }
      
      // 空行
      currentRow++
      
      // 基本信息行 - 使用合并单元格确保不被挤压
      const infoRow = worksheet.getRow(currentRow++)
      
      // 计算每个信息项占用的列数
      const colsPerInfo = Math.floor(tableDataCols / 4)
      
      // 记录编号
      infoRow.getCell(1).value = `记录编号：${pageData.recordNumber}`
      if (colsPerInfo > 1) {
        worksheet.mergeCells(currentRow - 1, 1, currentRow - 1, colsPerInfo)
      }
      
      // 设备编号
      if (tableDataCols >= 2) {
        const startCol = colsPerInfo + 1
        infoRow.getCell(startCol).value = `设备编号：${pageData.deviceNumber}`
        if (colsPerInfo > 1 && startCol + colsPerInfo - 1 <= tableDataCols) {
          worksheet.mergeCells(currentRow - 1, startCol, currentRow - 1, Math.min(startCol + colsPerInfo - 1, tableDataCols))
        }
      }
      
      // 环境温度
      if (tableDataCols >= 3) {
        const startCol = colsPerInfo * 2 + 1
        infoRow.getCell(startCol).value = `环境温度${pageData.temperature}℃`
        if (colsPerInfo > 1 && startCol + colsPerInfo - 1 <= tableDataCols) {
          worksheet.mergeCells(currentRow - 1, startCol, currentRow - 1, Math.min(startCol + colsPerInfo - 1, tableDataCols))
        }
      }
      
      // 页码信息
      if (tableDataCols >= 4) {
        const startCol = colsPerInfo * 3 + 1
        infoRow.getCell(startCol).value = `共${pageData.totalPagesInData}页第${pageData.pageIndexInData + 1}页`
        if (startCol + colsPerInfo - 1 <= tableDataCols) {
          worksheet.mergeCells(currentRow - 1, startCol, currentRow - 1, tableDataCols)
        }
      }
      
      infoRow.font = { size: 11 }
      infoRow.alignment = { horizontal: 'left', vertical: 'middle' }
      
      // 特殊属性（只在首页显示；检测对象为"默认"的项不导出）
      const exportSpecialAttributes = visibleSpecialAttributes(pageData)
      if (pageData.isFirstPage && exportSpecialAttributes.length > 0) {
        currentRow++ // 空行
        
        // 每行显示3个特殊属性，按行分组
        const attrsPerRow = 3
        const attrRows = Math.ceil(exportSpecialAttributes.length / attrsPerRow)
        const attrStartRow = currentRow
        
        for (let rowIndex = 0; rowIndex < attrRows; rowIndex++) {
          const attrRow = worksheet.getRow(currentRow++)
          const startIndex = rowIndex * attrsPerRow
          const endIndex = Math.min(startIndex + attrsPerRow, exportSpecialAttributes.length)
          
          // 在每行中分布3个属性，使用合适的列间距
          for (let i = startIndex; i < endIndex; i++) {
            const attr = exportSpecialAttributes[i]
            const colIndex = ((i - startIndex) * Math.floor(tableDataCols / attrsPerRow)) + 1
            attrRow.getCell(colIndex).value = `${attr.title}：${attr.value}`
          }
          
          attrRow.font = { size: 11 }
          attrRow.alignment = { horizontal: 'left', vertical: 'middle' }
        }
        
        // 只为特殊属性区域的外围添加边框
        const attrEndRow = currentRow - 1
        for (let row = attrStartRow; row <= attrEndRow; row++) {
          for (let col = 1; col <= tableDataCols; col++) {
            const cell = worksheet.getCell(row, col)
            const border: any = {}
            
            // 只在外围添加边框
            if (row === attrStartRow) border.top = { style: 'thin' }
            if (row === attrEndRow) border.bottom = { style: 'thin' }
            if (col === 1) border.left = { style: 'thin' }
            if (col === tableDataCols) border.right = { style: 'thin' }
            
            if (Object.keys(border).length > 0) {
              cell.border = border
            }
          }
        }
      }
      
      // 续页标识
      if (!pageData.isFirstPage) {
        const continueRow = worksheet.getRow(currentRow++)
        continueRow.getCell(1).value = '（续上页）'
        continueRow.font = { size: 11 }
        continueRow.alignment = { horizontal: 'center' }
      }
      
      // 空行
      currentRow++
      
      // 表格数据
      if (pageData.pageRows && pageData.pageRows.length > 0) {
        const tableStartRow = currentRow
        
        // 表头 - 分区名称行
        const sectionHeaderRow = worksheet.getRow(currentRow++)
        sectionHeaderRow.getCell(1).value = '序号'
        sectionHeaderRow.getCell(2).value = '楼号楼层'
        sectionHeaderRow.getCell(3).value = '轴线号'
        sectionHeaderRow.getCell(4).value = ''
        
        let colIndex = 5
        pageData.sections.forEach(section => {
          sectionHeaderRow.getCell(colIndex).value = section.name
          // 合并分区标题单元格
          if (section.columnCount > 1) {
            worksheet.mergeCells(currentRow - 1, colIndex, currentRow - 1, colIndex + section.columnCount - 1)
          }
          colIndex += section.columnCount
        })
        
        // 设置表头样式 - 只为实际列设置边框
        for (let col = 1; col <= tableDataCols; col++) {
          const cell = sectionHeaderRow.getCell(col)
          cell.font = { bold: true, size: 12 }
          cell.alignment = { horizontal: 'center', vertical: 'middle' }
          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          }
        }
        
        // 表头 - 横向标题行
        if (pageData.tableInfo?.horizontalHeaders) {
          const headerRow = worksheet.getRow(currentRow++)
          headerRow.getCell(1).value = ''
          headerRow.getCell(2).value = ''
          headerRow.getCell(3).value = ''
          headerRow.getCell(4).value = ''
          
          pageData.tableInfo.horizontalHeaders.forEach((header, index) => {
            headerRow.getCell(5 + index).value = header
          })
          
          // 设置表头样式 - 只为实际列设置边框
          for (let col = 1; col <= tableDataCols; col++) {
            const cell = headerRow.getCell(col)
            cell.font = { bold: true, size: 11 }
            cell.alignment = { horizontal: 'center', vertical: 'middle' }
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' }
            }
          }
        }
        
        // 数据行
        let rowIndex = 0

        while (rowIndex < pageData.pageRows.length) {
          const firstRow = pageData.pageRows[rowIndex]
          const groupRows: FlatTableRow[] = []

          while (
            rowIndex < pageData.pageRows.length &&
            pageData.pageRows[rowIndex].mainIndex === firstRow.mainIndex
          ) {
            groupRows.push(pageData.pageRows[rowIndex])
            rowIndex++
          }

          const singleAttrs = getMainSingleAttrsList(pageData, firstRow.mainIndex)
          const seqStartRow = currentRow

          if (singleAttrs.length > 0) {
            const singleAttrRowNumber = currentRow++
            const singleAttrRow = worksheet.getRow(singleAttrRowNumber)
            singleAttrRow.getCell(1).value = firstRow.mainIndex + 1
            singleAttrRow.getCell(2).value = `单条字段  ${formatSingleAttrsText(singleAttrs)}`
            worksheet.mergeCells(singleAttrRowNumber, 2, singleAttrRowNumber, tableDataCols)
            singleAttrRow.height = 22

            for (let col = 1; col <= tableDataCols; col++) {
              const cell = singleAttrRow.getCell(col)
              cell.font = { size: 10 }
              cell.alignment = { horizontal: col === 1 ? 'center' : 'left', vertical: 'middle', wrapText: true }
              cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
              }
            }
          }

          const dataStartRow = currentRow

          groupRows.forEach((row, groupRowIndex) => {
            const dataRow = worksheet.getRow(currentRow++)

            // 固定列：序号会在有单条字段时从单条字段行开始合并
            if (groupRowIndex === 0) {
              if (singleAttrs.length === 0) {
                dataRow.getCell(1).value = row.mainIndex + 1
              }
              dataRow.getCell(2).value = row.buildingFloor
              dataRow.getCell(3).value = row.axisNumber
            } else {
              dataRow.getCell(1).value = ''
              dataRow.getCell(2).value = ''
              dataRow.getCell(3).value = ''
            }

            // 纵向标题
            dataRow.getCell(4).value = row.verticalHeader

            // 数据单元格
            row.cells.forEach((cell, index) => {
              dataRow.getCell(5 + index).value = cell || ''
            })

            // 设置数据行样式 - 只为实际列设置边框
            for (let col = 1; col <= tableDataCols; col++) {
              const cell = dataRow.getCell(col)
              cell.font = { size: 11 }
              cell.alignment = { horizontal: 'center', vertical: 'middle' }
              cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
              }
            }
          })

          const dataEndRow = currentRow - 1
          const seqEndRow = dataEndRow

          if (seqEndRow > seqStartRow) {
            worksheet.mergeCells(seqStartRow, 1, seqEndRow, 1)
          }

          if (groupRows.length > 1) {
            worksheet.mergeCells(dataStartRow, 2, dataEndRow, 2)
            worksheet.mergeCells(dataStartRow, 3, dataEndRow, 3)
          }
        }
      }
      
      // 空行
      currentRow++

      // 页脚签名区 - 使用与页头相同的间隔分布
      const footerRow = worksheet.getRow(currentRow++)

      // 计算页脚项目数量（检验、计算（可选）、校核、记录、检验日期）
      const footerItems: string[] = [`检验：${pageData.inspector}`]
      if (pageData.showCalculator) {
        footerItems.push(`计算：${pageData.calculator}`)
      }
      footerItems.push(`校核：${pageData.reviewer}`)
      footerItems.push(`记录：${pageData.recorder}`)
      footerItems.push(`检验日期：${formatDate(pageData.inspectionDate)}`)

      // 按比例分配列位置，与页头保持一致
      const colsPerFooterItem = Math.floor(tableDataCols / footerItems.length)
      footerItems.forEach((item, index) => {
        const startCol = index * colsPerFooterItem + 1
        footerRow.getCell(startCol).value = item
        // 合并单元格使内容有足够空间
        const endCol = index === footerItems.length - 1 ? tableDataCols : (index + 1) * colsPerFooterItem
        if (endCol > startCol) {
          worksheet.mergeCells(currentRow - 1, startCol, currentRow - 1, endCol)
        }
      })

      footerRow.font = { size: 11 }
      
      // 设置列宽 - 严格按照表格数据列数设置
      worksheet.getColumn(1).width = 8   // 序号
      worksheet.getColumn(2).width = 15  // 楼号楼层
      worksheet.getColumn(3).width = 10  // 轴线号
      worksheet.getColumn(4).width = 12  // 纵向标题
      
      // 为数据列设置宽度
      if (pageData.tableInfo?.horizontalHeaders) {
        pageData.tableInfo.horizontalHeaders.forEach((_, index) => {
          worksheet.getColumn(5 + index).width = 12
        })
      }
      
      // 合并公司标题单元格 - 使用表格实际列数
      worksheet.mergeCells(3, 1, 3, tableDataCols)
      worksheet.mergeCells(4, 1, 4, tableDataCols)
      worksheet.mergeCells(5, 1, 5, tableDataCols)
      worksheet.mergeCells(6, 1, 6, tableDataCols)
      
      // 为合并后的标题单元格设置居中样式
      worksheet.getCell(3, 1).alignment = { horizontal: 'center', vertical: 'middle' }
      worksheet.getCell(4, 1).alignment = { horizontal: 'center', vertical: 'middle' }
      worksheet.getCell(5, 1).alignment = { horizontal: 'center', vertical: 'middle' }
      worksheet.getCell(6, 1).alignment = { horizontal: 'center', vertical: 'middle' }
    }
    
    // 生成文件名
    const firstPage = printData.value[0]
    const fileName = `${firstPage.projectName || '打印列表'}_${formatDateCode(firstPage.implementDate) || new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
    
    // 导出文件
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, fileName)
    
    ElMessage.success('Excel导出成功')
  } catch (error) {
    console.error('Excel导出失败:', error)
    ElMessage.error('Excel导出失败，请重试')
  }
}

onMounted(() => {
  loadPrintData()
})

watch(
  () => [route.path, route.query.cacheKey, route.query.id],
  () => {
    loadPrintData()
  }
)
</script>

<style scoped>
.print-list-page {
  position: relative;
  padding: 10px;
  background: #f5f5f5;
  min-height: 100vh;
}

/* 操作栏（打印时隐藏） */
.page-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.page-refresh-action {
  margin-left: auto;
}

.page-refresh-action :deep(.el-button) {
  width: 40px;
  height: 40px;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

/* 加载状态 */
.print-loading-overlay {
  position: absolute;
  inset: 50px 0 0;
  z-index: 10;
  padding: 24px;
  background: #f5f5f5;
}

/* 打印内容包装器 */
.print-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
}

.print-wrapper--preparing {
  visibility: hidden;
}

/* 打印内容区域 - A4竖向尺寸（默认） */
.print-content {
  background: #fff;
  width: 210mm;
  height: 297mm;
  padding: 10mm;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 横向内容区域 - A4横向尺寸 */
.print-content.landscape-content {
  width: 297mm;
  height: 210mm;
  min-height: 210mm;
  max-height: 210mm;
}

.print-content.landscape-content .data-table {
  font-size: 10px;
}

.print-content.landscape-content .data-table th,
.print-content.landscape-content .data-table td {
  padding: 0;
  line-height: 12px;
}

/* 页面主体内容区域 */
.page-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 允许 flex 子元素收缩 */
}

/* 页眉信息 */
.page-header-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
  flex-shrink: 0;
}

/* 公司标题区域 */
.company-header {
  text-align: center;
  margin-bottom: 6px;
  flex-shrink: 0;
}

.company-name {
  font-size: 18px;
  font-weight: bold;
  color: #000;
  margin-bottom: 1px;
}

.company-sub {
  font-size: 16px;
  color: #000;
  margin-bottom: 2px;
}

.report-title {
  font-size: 20px;
  font-weight: bold;
  color: #000;
  margin-bottom: 1px;
}

.report-code {
  font-size: 14px;
  color: #333;
}

/* 基本信息行 */
.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 14px;
  flex-shrink: 0;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-label {
  color: #333;
}

.info-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #000;
  min-width: 60px;
  border-bottom: 1px solid #000;
  margin: 0 4px;
  text-align: center;
  min-height: 20px;
  line-height: 1;
}

.empty-value {
  align-self: flex-end;
}

/* 页码信息 */
.page-info {
  font-size: 12px;
}

.page-value {
  min-width: 30px;
  margin: 0 2px;
}

/* 特殊属性行 */
.special-attrs-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 6px;
  padding: 4px 8px;
  border: 1px solid #000;
  flex-shrink: 0;
}

.special-attr-item {
  display: flex;
  align-items: center;
  width: calc(33.33% - 14px);
  margin-right: 20px;
  margin-bottom: 2px;
}

.special-attr-item:nth-child(3n) {
  margin-right: 0;
}

.attr-label {
  font-size: 14px;
  color: #333;
  margin-right: 8px;
  white-space: nowrap;
}

.attr-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #000;
  min-width: 80px;
  flex: 1;
  border-bottom: 1px solid #000;
  text-align: center;
  min-height: 20px;
  line-height: 1;
}

/* 续页标识 */
.continue-page-hint {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  text-align: center;
  flex-shrink: 0;
}

/* 表格容器 - 限制高度防止溢出 */
.table-container {
  flex: 1;
  min-height: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
}

/* 数据表格 */
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  flex: 0 0 auto;
}

.data-table th,
.data-table td {
  border: 1px solid #000;
  padding: 2px 4px;
  text-align: center;
  vertical-align: middle;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.data-table th {
  background: #fff;
  font-weight: normal;
  color: #000;
  line-height: 1.4;
  min-height: 24px;
  max-height: 60px;  /* 限制表头最大高度，防止过度换行 */
  height: auto;
  white-space: normal;
  overflow: hidden;  /* 超出部分隐藏 */
  text-overflow: ellipsis;
  font-size: 11px;  /* 稍微缩小字体，减少换行 */
}

.data-table td {
  min-height: 16px;
  font-size: 11px;
}

.data-table .single-attrs-cell {
  padding: 0;
  max-height: none;
  overflow: visible;
}

.single-attrs-row .col-seq {
  vertical-align: middle;
}

.single-attrs-row {
  height: 18px;
}

.single-attrs-row td {
  max-height: none;
  overflow: visible;
}

.single-attrs-content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 1px 12px;
  padding: 0;
  box-sizing: border-box;
  line-height: 14px;
}

.single-attrs-title {
  font-size: 12px;
  color: #000;
  font-weight: normal;
  white-space: nowrap;
}

.single-attrs-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.single-attrs-label {
  color: #333;
  font-size: 12px;
  white-space: nowrap;
}

.single-attrs-label::after {
  content: '：';
}

.single-attrs-value {
  color: #000;
  font-size: 12px;
  min-width: 44px;
  text-align: center;
  min-height: 14px;
  line-height: 1;
  padding: 0 4px;
  border-bottom: 1px solid #000;
}

.data-table .row-header {
  width: 80px;
  font-weight: bold;
}

.data-table .col-seq {
  width: 40px;
}

.data-table .col-building {
  width: 70px;
}

.data-table .col-axis {
  width: 60px;
}

.data-table .col-vertical {
  width: 80px;
}

/* 固定列与动态列之间的分隔线 */
.data-table .fixed-col-end {
  border-right: 2px solid #000;
}

/* 表头轴线号列也加粗右边框 */
.data-table th.col-axis {
  border-right: 2px solid #000;
}

/* 每个主表（分表）之间的分隔线 */
.data-table .main-row-start td {
  border-top: 2px solid #000;
}

/* 分区分隔线 */
.data-table .section-start {
  border-left: 2px solid #000;
}

/* 分区名称表头 */
.section-name-header {
  font-weight: bold;
  background: #fff;
}

/* 页脚签名区 */
.page-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  font-size: 12px;
  flex-wrap: wrap;
  gap: 6px;
  flex-shrink: 0; /* 页脚不收缩 */
  min-height: 26px; /* 确保页脚有最小高度 */
  border-top: none;
}

.footer-item {
  display: flex;
  align-items: center;
}

.footer-label {
  color: #333;
}

.footer-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  border-bottom: 1px solid #000;
  margin: 0 3px;
  height: 14px;
  text-align: center;
}

.date-item {
  display: flex;
  align-items: center;
}

/* 打印样式 - 支持混合方向 */
@media print {
  .no-print {
    display: none !important;
  }

  .print-list-page {
    padding: 0;
    background: #fff;
  }

  .print-wrapper {
    gap: 0;
  }

  .print-content {
    box-shadow: none;
    padding: 10mm;
    page-break-after: always;
    page-break-inside: avoid;
    page: portrait-page;
  }
  
  .print-content.landscape-content {
    page: landscape-page;
  }

  .print-content:last-child {
    page-break-after: avoid;
  }

  @page portrait-page {
    size: A4 portrait;
    margin: 0;
  }
  
  @page landscape-page {
    size: A4 landscape;
    margin: 0;
  }
}

</style>
